import { sanitizeValue, roundValue, flatten } from './helpers.js';

/**
 * @typedef {Object} Share
 * @property {String} [key] Mandatory when fitting
 * @property {Number} [statutoryValue] Value used if there is no last will ("Gesetzlicher Erbteil")
 * @property {Number} [compulsoryValue] Minimal value required by law ("Pflichtteil")
 * @property {Number} [maximalValue] Used for free share
 * @property {Number} [value] Current value
 * @property {Number} [targetValue] Fitting process: Wanted value
 * @property {Boolean} [isFixed] Fitting process: Treat as fixed, do not attempt to reduce to compulsaryValue
 * @property {Array.<Share>} [parts] Descendants
 */

/**
 * Calculate statutory and compulsory shares
 * @param {Array.<Share>} inputShares
 * @param {Object} [options]
 * @param {Boolean} [options.rounded] Whether to round to 0.25
 * @return {Array.<Share>}
 */
function calculate(inputShares = [], options = {}) {
	const shares = [...inputShares]
		// Remove free share
		.filter(share => !['free', 'government'].includes(share.key));

	// Case 1: No heirs (custom shares are ignored)
	if (!shares.length || !shares.find(share => share.key !== 'custom')) {
		return _finalizeCalculate(
			[
				{
					key: 'government',
					statutoryValue: 100,
					compulsoryValue: 0
				}
			],
			inputShares,
			options
		);
	}

	const spouse = shares.find(share => share.key === 'spouse');
	const firstParentela = shares.find(share => share.key === 'firstParentela');
	const secondParentela = shares.find(share => share.key === 'secondParentela');
	const thirdParentela = shares.find(share => share.key === 'thirdParentela');

	// Case 2: Spouse (or registered partner)
	if (spouse) {
		// Case 2.1: Spouse and children
		if (firstParentela) {
			return _finalizeCalculate(
				[
					{ key: 'spouse', statutoryValue: 50, compulsoryValue: 25 },
					{ key: 'firstParentela', statutoryValue: 50, compulsoryValue: 25 }
				],
				inputShares,
				options
			);
		}

		// Case 2.2: Spouse and 2nd parentela
		if (secondParentela) {
			const spouse = {
				key: 'spouse',
				statutoryValue: 75,
				compulsoryValue: 37.5
			};

			// Single parent or no parents, siblings and descendants
			const siblings = secondParentela.parts?.find(share => share.parts)?.parts;

			if (siblings && siblings.length) {
				// Case 2.2.1: Single parent
				if (secondParentela.parts.length === 2) {
					return _finalizeCalculate(
						[
							spouse,
							{ key: 'secondParentela', statutoryValue: 25, compulsoryValue: 0 }
						],
						inputShares,
						options
					);
				}

				// Case 2.2.2: No parents
				return _finalizeCalculate(
					[spouse, { key: 'secondParentela', statutoryValue: 25, compulsoryValue: 0 }],
					inputShares,
					options
				);
			}

			// Case 2.2.3: No siblings
			return _finalizeCalculate(
				[spouse, { key: 'secondParentela', statutoryValue: 25, compulsoryValue: 0 }],
				inputShares,
				options
			);
		}

		// Case 2.3: Spouse only
		return _finalizeCalculate(
			[{ key: 'spouse', statutoryValue: 100, compulsoryValue: 50 }],
			inputShares,
			options
		);
	}

	// Case 3: 1st parentela: Children and descendants
	if (firstParentela) {
		return _finalizeCalculate(
			[{ key: 'firstParentela', statutoryValue: 100, compulsoryValue: 50 }],
			inputShares,
			options
		);
	}

	// Case 4: 2nd parentela: Parents and descendants
	if (secondParentela) {
		// Case 4.1: Single parent or no parents, siblings and descendants
		const siblings = secondParentela.parts?.find(share => share.parts)?.parts;

		if (siblings && siblings.length) {
			// Case 4.1.1: Single parent
			if (secondParentela.parts.length === 2) {
				return _finalizeCalculate(
					[
						{
							key: 'secondParentela',
							statutoryValue: 100,
							compulsoryValue: 0
						}
					],
					inputShares,
					options
				);
			}

			// Case 4.1.2: No parents
			return _finalizeCalculate(
				[
					{
						key: 'secondParentela',
						statutoryValue: 100,
						compulsoryValue: 0
					}
				],
				inputShares,
				options
			);
		}

		// Case 4.2: Both parents
		return _finalizeCalculate(
			[
				{
					key: 'secondParentela',
					statutoryValue: 100,
					compulsoryValue: 0
				}
			],
			inputShares,
			options
		);
	}

	// Case 5: 3rd parentela: Grandparents and descendants
	if (thirdParentela) {
		return _finalizeCalculate(
			[
				{
					key: 'thirdParentela',
					statutoryValue: 100,
					compulsoryValue: 0
				}
			],
			inputShares,
			options
		);
	}

	return _finalizeCalculate([], inputShares, options);
}

/**
 * Calculate free quota and handle sub shares
 * @param {Array.<Share>} calculatedShares
 * @param {Array.<Share>} inputShares
 * @param {Object} [options]
 * @param {Boolean} [options.rounded] Whether to round
 * @return {Array.<Share>}
 */
function _finalizeCalculate(calculatedShares = [], inputShares = [], options = {}) {
	let finalizedShares = [...calculatedShares];

	// Add empty shares
	inputShares.forEach(share => {
		if (
			!finalizedShares.find(finalizedShare => finalizedShare.key === share.key) &&
			share.key !== 'free'
		) {
			finalizedShares.push({
				key: share.key,
				statutoryValue: 0,
				compulsoryValue: 0
			});
		}
	});

	// Handle nested shares and calculate free quota based on minimal values
	let maximalFreeValue = 100;

	finalizedShares = finalizedShares.map(finalizedShare => {
		const inputShare = inputShares.find(inputShare => inputShare.key === finalizedShare.key);
		const inheritCompulsoryValue = ['firstParentela'].includes(finalizedShare.key);

		// Merge calculated share with input share so we inherit all properties
		const share = {
			...inputShare,
			...finalizedShare
		};

		// Calculate parts
		if (share.parts) {
			share.parts = _calculateParts(share, {
				inheritCompulsoryValue,
				rounded: options.rounded
			});
		}

		// Reduce free value by compulsory value
		maximalFreeValue -= sanitizeValue(share.compulsoryValue);

		return share;
	});

	// Add free share
	const freeInputShare = inputShares.find(inputShare => inputShare.key === 'free');

	finalizedShares.push({
		...freeInputShare,
		key: 'free',
		maximalValue: maximalFreeValue,
		statutoryValue: 0,
		compulsoryValue: 0
	});

	return finalizedShares;
}

/**
 * Calculate parts of shares
 * @param {Share} share
 * @param {Object} [options]
 * @param {Boolean} [options.inheritCompulsoryValue] Whether the compulsory value is passed on to parts
 * @param {Boolean} [options.rounded] Whether to round
 * @return {Array.<Share>}
 */
function _calculateParts(share, options = {}) {
	if (!share.parts) {
		return null;
	}

	const { inheritCompulsoryValue, rounded } = options;

	// Count parts which are relevant for dividing values
	const compulsoryParts = share.parts.filter(
		part => inheritCompulsoryValue || !part.parts
	).length;

	// Divide statutory and compulsory values
	const calculatedParts = share.parts.map(part => {
		// Compulsory share is only kept if we are in the first parentela or if there is no further division
		const keepCompulsoryValue = inheritCompulsoryValue || !part.parts;

		const statutoryValue = share.statutoryValue / share.parts.length;
		const compulsoryValue = keepCompulsoryValue ? share.compulsoryValue / compulsoryParts : 0;

		// Optionally round values
		const calculatedPart = {
			...part,
			statutoryValue: rounded ? roundValue(statutoryValue) : statutoryValue,
			compulsoryValue: rounded ? roundValue(compulsoryValue) : compulsoryValue
		};

		// Traverse
		if (calculatedPart.parts) {
			calculatedPart.parts = _calculateParts(calculatedPart, options);
		}

		return calculatedPart;
	});

	return calculatedParts;
}

/**
 * Fit shares based on target value
 * NOTE: Only the first share group with a `targetValue` is respected
 * @param {Array.<Share>} shares
 * @param {Object} [options]
 * @param {Boolean} [options.rounded] Whether to round
 * @return {Array.<Share>}
 */
function fit(shares, options = {}) {
	const calculatedShares = calculate(shares);
	const fittedShares = [...calculatedShares];

	// Flatten share groups
	const flattenedShares = flatten(calculatedShares);

	// Find target share, return if there is none
	const targetShare = flattenedShares.find(share => share.targetValue !== undefined);

	if (!targetShare) {
		return fittedShares;
	}

	// Calculate available amounts
	const { maxAvailable, freelyAvailable } = flattenedShares.reduce(
		({ maxAvailable, freelyAvailable }, share) => {
			if (share.key !== 'free') {
				if (share.targetValue !== undefined || share.isFixed) {
					maxAvailable -= sanitizeValue(share.value);
				} else {
					maxAvailable -= share.compulsoryValue;
				}

				freelyAvailable -= sanitizeValue(share.value);
			}

			return { maxAvailable, freelyAvailable };
		},
		{ maxAvailable: 100, freelyAvailable: 100 }
	);

	const currentTargetValue = sanitizeValue(targetShare.value);
	// Validate target value
	const validatedTargetValue = Math.max(
		// The lower limit is the compulsory value
		targetShare.compulsoryValue,
		// The upper limit is the current value plus the maximum change (or 100, whatever is smaller)
		Math.min(targetShare.targetValue, currentTargetValue + maxAvailable, 100)
	);

	// Calculate the change and the necessary compensation of other shares
	const targetDiff = validatedTargetValue - currentTargetValue;
	const compensation = Math.max(targetDiff - freelyAvailable, 0);

	// Fit shares
	const flatFittedShares = flattenedShares.map(share => {
		const fittedShare = { ...share };

		// Set new value to target share
		if (share.targetValue !== undefined) {
			fittedShare.value = sanitizeValue(fittedShare.value) + targetDiff;
		}

		if (compensation) {
			// Case 2: We need to compensate others
			if (fittedShare.key === 'free') {
				fittedShare.value = 0;
			} else if (fittedShare.targetValue === undefined && !share.isFixed) {
				// Check whether this share can be reduced
				const value = sanitizeValue(fittedShare.value);
				const possibleReduction = value - fittedShare.compulsoryValue;

				if (possibleReduction) {
					// Calculate share of total compensation
					const reduction =
						(compensation / (maxAvailable - freelyAvailable)) * possibleReduction;

					fittedShare.value = value - reduction;
				}
			}
		} else {
			// Case 1: We can just use the currently available free share
			if (fittedShare.key === 'free') {
				const freeValue = sanitizeValue(fittedShare.value) - targetDiff;

				fittedShare.value = Math.max(freeValue, 0);
			}
		}

		return fittedShare;
	});

	// Set new values
	let updatedShares = fittedShares.map(fittedShare => {
		const share = _updateShare(fittedShare, flatFittedShares, options);

		return share;
	});

	// Correct rounding in free share
	if (options.rounded) {
		const sum = updatedShares.reduce((acc, share) => {
			if (share.key !== 'free') {
				acc += share.value;
			}

			return acc;
		}, 0);

		updatedShares = updatedShares.map(share => {
			if (share.key === 'free') {
				share.value = roundValue(100 - sum, { rough: true });
			}

			return share;
		});
	}

	return updatedShares;
}

/**
 * Set new values based on fitted shares
 * @param {Share} share
 * @param {Array.<Share>} flatFittedShares
 * @param {Object} [options]
 * @param {Boolean} [options.rounded] Whether to round
 * @return {Share}
 */
function _updateShare(share, flatFittedShares, options = {}) {
	const updatedShare = {
		...share
	};

	if (!updatedShare.parts) {
		// Find fitted share by key
		const fittedShare = flatFittedShares.find(fittedShare => share.key === fittedShare.key);

		// Select fitted value if found
		if (fittedShare) {
			updatedShare.value = options.rounded
				? roundValue(fittedShare.value)
				: fittedShare.value;
		}

		// Clean up
		delete updatedShare.targetValue;

		return updatedShare;
	}

	// Traverse
	updatedShare.parts = updatedShare.parts.map(part => {
		const updatedPart = _updateShare(part, flatFittedShares, options);

		return updatedPart;
	});

	// Sum up parts
	const sum = updatedShare.parts.reduce((acc, part) => {
		acc += part.value;

		return acc;
	}, 0);

	updatedShare.value = options.rounded ? roundValue(sum, { rough: true }) : sum;

	return updatedShare;
}

export { calculate, fit };
